import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { useAuth } from '../../context/AuthContext';
import { adminEmailsService } from '../../services/adminEmailsService';
import { useStrings } from '../../i18n';
import PanchTatvaButton from '../../components/PanchTatvaButton';

export default function AdminEmailsWhitelist() {
  const { getIdToken, userInfo } = useAuth();
  const strings = useStrings();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    role: 'admin',
    reason: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (userInfo?.role !== 'superadmin') {
      setError(strings.admin?.superadminOnly || 'Only superadmins can manage admin emails');
      return;
    }
    fetchEmails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchTerm, statusFilter, userInfo]);

  const fetchEmails = async () => {
    try {
      setLoading(true);
      const idToken = await getIdToken();
      const response = await adminEmailsService.listWhitelist(
        {
          page,
          limit: 10,
          search: searchTerm,
          status: statusFilter,
        },
        idToken
      );
      setEmails(response.data || []);
      setTotal(response.pagination?.total || 0);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load emails');
      console.error('Error fetching emails:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmail = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.email.includes('@')) {
      setError(strings.admin?.invalidEmail || 'Invalid email address');
      return;
    }

    try {
      setLoading(true);
      const idToken = await getIdToken();
      const response = await adminEmailsService.addWhitelist([formData], idToken);

      if (response.failed && response.failed.length > 0) {
        setError(response.failed[0]?.error || 'Failed to add email');
      } else {
        setSuccess(strings.admin?.emailAdded || 'Email added successfully');
        setFormData({ email: '', fullName: '', role: 'admin', reason: '' });
        setShowAddForm(false);
        setPage(1);
        fetchEmails();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.message || 'Failed to add email');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveEmail = async (emailId) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(strings.admin?.confirmRemove || 'Are you sure you want to remove this email?')) {
      return;
    }

    try {
      setLoading(true);
      const idToken = await getIdToken();
      await adminEmailsService.removeWhitelist(emailId, {}, idToken);
      setSuccess(strings.admin?.emailRemoved || 'Email removed successfully');
      fetchEmails();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to remove email');
    } finally {
      setLoading(false);
    }
  };

  // Only superadmins can access this page
  if (userInfo?.role !== 'superadmin') {
    return (
      <div className="w-full max-w-5xl mx-auto px-4 md:px-6 py-12 md:py-20 fade-section show">
        <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700">
            {strings.admin?.superadminOnly || 'Only superadmins can manage admin emails'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto px-4 md:px-6 py-12 md:py-20 fade-section show">
      {/* Back Button */}
      <Link to="/admin" className="inline-flex items-center text-secondary hover:text-secondary/80 mb-6 transition-colors">
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        {strings.admin?.backToDashboard || 'Back to Dashboard'}
      </Link>

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-heading text-headingText mb-2">
          {strings.admin?.adminEmails || 'Admin Email Whitelist'}
        </h1>
        <p className="text-textSecondary">
          {strings.admin?.emailWhitelistDesc ||
            'Manage which emails can auto-onboard as admins when they sign up'}
        </p>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          {success}
        </div>
      )}

      {/* Add New Email Form */}
      {showAddForm ? (
        <div className="mb-8 p-6 bg-cardBg rounded-xl border border-secondary/20">
          <h2 className="text-xl font-heading text-headingText mb-4">
            {strings.admin?.addNewEmail || 'Add New Admin Email'}
          </h2>
          <form onSubmit={handleAddEmail} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-bodyText mb-2">
                {strings.admin?.email || 'Email Address'} *
              </label>
              <input
                type="email"
                placeholder="admin@panch-tatva.co.in"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value.toLowerCase().trim() })
                }
                className="w-full px-4 py-2 border border-secondary/20 rounded-lg bg-surface text-bodyText focus:outline-none focus:border-secondary"
                required
              />
              <p className="text-xs text-textSecondary mt-1">
                {strings.admin?.emailHint ||
                  'Use company domain: @panch-tatva.co.in for easier management'}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-bodyText mb-2">
                  {strings.admin?.fullName || 'Full Name'}
                </label>
                <input
                  type="text"
                  placeholder="John Doe"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full px-4 py-2 border border-secondary/20 rounded-lg bg-surface text-bodyText focus:outline-none focus:border-secondary"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-bodyText mb-2">
                  {strings.admin?.role || 'Role'} *
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-4 py-2 border border-secondary/20 rounded-lg bg-surface text-bodyText focus:outline-none focus:border-secondary"
                >
                  <option value="admin">
                    {strings.admin?.adminRole || 'Admin'}
                  </option>
                  <option value="superadmin">
                    {strings.admin?.superadminRole || 'Superadmin'}
                  </option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-bodyText mb-2">
                {strings.admin?.reason || 'Reason / Department'}
              </label>
              <input
                type="text"
                placeholder={strings.admin?.reasonExample || 'e.g., Operations Manager'}
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                className="w-full px-4 py-2 border border-secondary/20 rounded-lg bg-surface text-bodyText focus:outline-none focus:border-secondary"
              />
              <p className="text-xs text-textSecondary mt-1">
                {strings.admin?.reasonHint ||
                  'Internal note: helps track why this person was added'}
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <PanchTatvaButton variant="primary" type="submit" size="default">
                {strings.admin?.addEmail || 'Add Email'}
              </PanchTatvaButton>
              <PanchTatvaButton
                variant="outline"
                type="button"
                size="default"
                onClick={() => setShowAddForm(false)}
              >
                {strings.common?.cancel || 'Cancel'}
              </PanchTatvaButton>
            </div>
          </form>
        </div>
      ) : (
        <div className="mb-8">
          <PanchTatvaButton
            variant="primary"
            onClick={() => setShowAddForm(true)}
            size="default"
          >
            {strings.admin?.addNewAdmin || '+ Add New Admin Email'}
          </PanchTatvaButton>
        </div>
      )}

      {/* Filters Section */}
      <div className="mb-6 space-y-4">
        <div>
          <label className="block text-sm font-semibold text-bodyText mb-2">
            {strings.admin?.search || 'Search'}
          </label>
          <input
            type="text"
            placeholder={strings.admin?.searchEmails || 'Search by email or name...'}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            className="w-full px-4 py-2 border border-secondary/20 rounded-lg bg-surface text-bodyText focus:outline-none focus:border-secondary"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-bodyText mb-2">
            {strings.admin?.status || 'Status'}
          </label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="w-full px-4 py-2 border border-secondary/20 rounded-lg bg-surface text-bodyText focus:outline-none focus:border-secondary"
          >
            <option value="all">{strings.admin?.emailAllStatus || 'All Status'}</option>
            <option value="pending">
              {strings.admin?.emailPending || 'Pending (Not Signed Up Yet)'}
            </option>
            <option value="active">
              {strings.admin?.emailActive || 'Active (User Signed Up)'}
            </option>
            <option value="inactive">
              {strings.admin?.emailInactive || 'Inactive (Removed)'}
            </option>
          </select>
        </div>
      </div>

      {/* Emails List */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-bodyText">{strings.admin?.loading || 'Loading...'}</p>
        </div>
      ) : emails.length === 0 ? (
        <div className="text-center py-12 bg-cardBg rounded-lg border border-secondary/10">
          <p className="text-textSecondary">
            {strings.admin?.noEmails || 'No admin emails whitelisted yet'}
          </p>
          {!showAddForm && (
            <PanchTatvaButton
              variant="outline"
              onClick={() => setShowAddForm(true)}
              size="small"
              className="mt-4"
            >
              {strings.admin?.addFirst || 'Add First Email'}
            </PanchTatvaButton>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {emails.map((item) => (
            <div
              key={item._id}
              className="p-4 bg-cardBg rounded-lg border border-secondary/10 flex justify-between items-start hover:border-secondary/20 transition-colors"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-bodyText">{item.email}</p>
                  <span
                    className={`text-xs px-2 py-1 rounded-full font-medium ${
                      item.status === 'active'
                        ? 'bg-green-50 text-green-700'
                        : item.status === 'pending'
                          ? 'bg-yellow-50 text-yellow-700'
                          : 'bg-gray-50 text-gray-700'
                    }`}
                  >
                    {item.status === 'active'
                      ? strings.admin?.emailActive || 'Active'
                      : item.status === 'pending'
                        ? strings.admin?.emailPending || 'Pending'
                        : strings.admin?.emailInactive || 'Inactive'}
                  </span>
                </div>

                {item.fullName && (
                  <p className="text-sm text-bodyText mt-1">{item.fullName}</p>
                )}

                <div className="flex gap-3 mt-2 text-xs text-textSecondary">
                  <span>
                    <strong>{strings.admin?.role || 'Role'}:</strong> {item.role}
                  </span>
                  {item.reason && (
                    <span>
                      <strong>{strings.admin?.reason || 'Reason'}:</strong> {item.reason}
                    </span>
                  )}
                </div>

                {item.activatedAt && (
                  <p className="text-xs text-textSecondary mt-2">
                    {strings.admin?.activatedOn || 'Activated on'}:{' '}
                    {new Date(item.activatedAt).toLocaleDateString()}
                  </p>
                )}
              </div>

              <button
                onClick={() => handleRemoveEmail(item._id)}
                className="ml-4 px-3 py-1 bg-red-50 text-red-600 rounded text-xs font-medium hover:bg-red-100 transition-colors"
              >
                {strings.common?.remove || 'Remove'}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > 10 && (
        <div className="mt-8 flex justify-center items-center gap-3">
          <button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            className="px-4 py-2 border border-secondary/20 rounded-lg text-bodyText disabled:opacity-50 disabled:cursor-not-allowed hover:border-secondary transition-colors"
          >
            {strings.common?.previous || 'Previous'}
          </button>

          <span className="px-4 py-2 text-textSecondary text-sm">
            {page} / {Math.ceil(total / 10)}
          </span>

          <button
            disabled={page >= Math.ceil(total / 10)}
            onClick={() => setPage(page + 1)}
            className="px-4 py-2 border border-secondary/20 rounded-lg text-bodyText disabled:opacity-50 disabled:cursor-not-allowed hover:border-secondary transition-colors"
          >
            {strings.common?.next || 'Next'}
          </button>
        </div>
      )}

      {/* Info Box */}
      <div className="mt-12 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-900">
          <strong>{strings.admin?.howItWorks || 'How it works'}: </strong> When a user signs up
          with an email on this whitelist, they automatically get the assigned admin role. No
          manual intervention needed!
        </p>
      </div>
    </div>
  );
}
